import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import * as Icons from "lucide-react";
import { ArrowRight, Check, ExternalLink } from "lucide-react";
import { Reveal, SectionHeading } from "@/components/Shared";
import { LeadForm } from "@/components/LeadForm";
import {
  PARTNER_BENEFITS,
  AMAZON_CAPABILITIES,
  WHAT_WE_LOOK_FOR,
  PROCESS_STEPS,
  STATS,
  MEDIA,
} from "@/data/site";
import { useSeo, SERVICE_SCHEMA } from "@/components/Seo";

function Icon({ name, ...props }) {
  const C = Icons[name] || Icons.Circle;
  return <C {...props} />;
}

export default function Home() {
  useSeo({
    path: "/",
    title: "National Product Distribution Company",
    description:
      "Hays Holdings is a national product distribution company helping brands, manufacturers, and CPG companies expand into U.S. retail. Your trusted nationwide wholesale and retail distribution partner.",
    keywords:
      "product distributor, national distributor, U.S. distributor, distribution company, distribution services, wholesale distribution, retail distribution, consumer goods distributor, CPG distributor, manufacturer distribution, distribution partner, brand distribution, national sales distribution, wholesale distributor, product distribution company",
    extraJsonLd: [SERVICE_SCHEMA],
  });
  return (
    <div data-testid="home-page">
      {/* SECTION 1: HERO (light) */}
      <section className="relative overflow-hidden bg-white">
        <div className="container-pn grid grid-cols-1 items-center gap-12 py-16 md:py-24 lg:grid-cols-12">
          <div className="lg:col-span-6">
            <motion.p
              initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }}
              className="label-eyebrow"
            >
              For Manufacturers & Product Brands
            </motion.p>
            <motion.h1
              initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6, delay: 0.08 }}
              className="mt-5 font-heading text-4xl font-black leading-[1.05] tracking-tight text-navy sm:text-5xl lg:text-6xl"
            >
              Your Distribution & Amazon Growth Partner
            </motion.h1>
            <motion.p
              initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6, delay: 0.16 }}
              className="mt-6 max-w-xl text-base leading-relaxed text-foreground/70 sm:text-lg"
            >
              Hays Holdings partners with manufacturers to expand distribution, grow Amazon sales, improve
              marketplace performance, and unlock new revenue opportunities through strategic channel
              management and fulfillment expertise.
            </motion.p>
            <motion.div
              initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6, delay: 0.24 }}
              className="mt-9 flex flex-col gap-3 sm:flex-row"
            >
              <Link to="/contact" data-testid="hero-cta-call" className="group flex items-center justify-center gap-2 bg-orange px-7 py-4 text-sm font-semibold text-white transition-colors hover:bg-orange-dark">
                Schedule a Discovery Call
                <ArrowRight size={16} className="transition-transform group-hover:translate-x-1" />
              </Link>
            </motion.div>
          </div>
          <div className="lg:col-span-6">
            <motion.div
              initial={{ opacity: 0, scale: 0.96 }} animate={{ opacity: 1, scale: 1 }} transition={{ duration: 0.7, delay: 0.2 }}
              className="relative"
            >
              <div className="overflow-hidden border border-border">
                <img src={MEDIA.hero} alt="National product distribution warehouse and logistics operations for brands and manufacturers" className="h-[420px] w-full object-cover" />
              </div>
              <div className="absolute -bottom-6 -left-6 hidden border border-border bg-white p-5 shadow-lg sm:block">
                <p className="font-heading text-2xl font-black text-orange">Amazon</p>
                <p className="text-xs font-semibold uppercase tracking-wider text-foreground/60">Marketplace Experts</p>
              </div>
            </motion.div>
          </div>
        </div>
        {/* stat strip */}
        <div className="border-y border-border bg-surface">
          <div className="container-pn grid grid-cols-2 gap-px lg:grid-cols-4">
            {STATS.map((s) => (
              <div key={s.label} className="bg-surface px-4 py-6 text-center">
                <p className="font-heading text-xl font-black text-navy sm:text-2xl">{s.value}</p>
                <p className="mt-1 text-xs uppercase tracking-wider text-foreground/50">{s.label}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* SECTION 2: WHY BRANDS PARTNER */}
      <section className="bg-white py-20 md:py-28" data-testid="why-section">
        <div className="container-pn">
          <Reveal>
            <SectionHeading eyebrow="The Hays Holdings Advantage" title="Why Brands Partner With Hays Holdings" />
          </Reveal>
          <div className="mt-12 grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3">
            {PARTNER_BENEFITS.map((b, i) => (
              <Reveal key={b.title} delay={i * 0.05}>
                <div className="group h-full border border-border bg-white p-7 transition-all duration-200 hover:-translate-y-1 hover:border-brand hover:shadow-lg">
                  <div className="flex h-12 w-12 items-center justify-center bg-surface text-brand transition-colors group-hover:bg-brand group-hover:text-white">
                    <Icon name={b.icon} size={22} />
                  </div>
                  <h3 className="mt-5 font-heading text-lg font-semibold text-navy">{b.title}</h3>
                  <p className="mt-2 text-sm leading-relaxed text-foreground/70">{b.desc}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* SECTION 3: WIN ON AMAZON */}
      <section className="bg-surface py-20 md:py-28" data-testid="amazon-section">
        <div className="container-pn">
          <div className="grid grid-cols-1 items-center gap-14 lg:grid-cols-2">
            <Reveal>
              <p className="label-eyebrow text-orange">Our Competitive Advantage</p>
              <h2 className="mt-3 font-heading text-3xl font-bold tracking-tight text-navy sm:text-4xl">
                How We Sell on Amazon & Through Our Distribution Channels
              </h2>
              <p className="mt-5 max-w-lg text-base leading-relaxed text-foreground/70">
                We don't just stock your products — we actively sell them. We move volume on Amazon and
                through our own national distribution channels, turning your catalog into a
                high-performing, well-protected, and rapidly growing revenue stream.
              </p>
              <div className="mt-8 overflow-hidden border border-border">
                <img src={MEDIA.amazon} alt="Wholesale and retail product distribution fulfillment and packaging" className="h-64 w-full object-cover" />
              </div>
            </Reveal>
            <Reveal delay={0.1}>
              <div className="grid grid-cols-1 gap-px border border-border bg-border sm:grid-cols-2">
                {AMAZON_CAPABILITIES.map((c) => (
                  <div key={c.title} className="bg-white p-6">
                    <Icon name={c.icon} size={24} className="text-orange" />
                    <h3 className="mt-3 font-heading text-base font-semibold text-navy">{c.title}</h3>
                    <p className="mt-1.5 text-sm leading-relaxed text-foreground/70">{c.desc}</p>
                  </div>
                ))}
              </div>
            </Reveal>
          </div>
        </div>
      </section>

      {/* SECTION 4: TRACK RECORD */}
      <section className="bg-white py-20 md:py-28" data-testid="track-record-section">
        <div className="container-pn">
          <div className="relative overflow-hidden border border-border bg-surface px-8 py-16 text-center md:px-16 md:py-20">
            <Reveal>
              <p className="label-eyebrow text-orange">Proven Track Record</p>
              <div className="mt-6 flex items-end justify-center gap-3">
                <span className="font-heading text-7xl font-black leading-none tracking-tighter text-navy sm:text-8xl">100</span>
                <span className="mb-2 font-heading text-5xl font-black leading-none text-orange sm:text-6xl">+</span>
              </div>
              <h2 className="mx-auto mt-6 max-w-3xl font-heading text-3xl font-bold tracking-tight text-navy sm:text-4xl">
                100+ brands worked with. One distribution playbook, mastered.
              </h2>
              <p className="mx-auto mt-5 max-w-2xl text-base leading-relaxed text-foreground/70">
                Every brand we've partnered with has sharpened how we sell, ship, and scale. After 100+
                brands across Amazon and our own distribution channels, we don't guess at distribution —
                we've turned it into a science. Your products get a partner that has already solved the
                hard parts.
              </p>
              <div className="mx-auto mt-10 grid max-w-2xl grid-cols-1 gap-px border border-border bg-border sm:grid-cols-3">
                {[
                  ["100+", "Brands Partnered"],
                  ["$12M+", "Amazon Revenue"],
                  ["1M+", "Products Moved"],
                ].map(([v, l]) => (
                  <div key={l} className="bg-surface px-4 py-6">
                    <p className="font-heading text-2xl font-black text-navy">{v}</p>
                    <p className="mt-1 text-xs uppercase tracking-wider text-foreground/50">{l}</p>
                  </div>
                ))}
              </div>
              <div className="mt-10">
                <Link to="/contact" data-testid="partners-cta" className="inline-flex items-center gap-2 bg-orange px-7 py-4 text-sm font-semibold text-white transition-colors hover:bg-orange-dark">
                  Become a Brand Partner <ArrowRight size={16} />
                </Link>
              </div>
            </Reveal>
          </div>
        </div>
      </section>

      {/* SECTION 5: WHAT WE LOOK FOR */}
      <section className="bg-surface py-20 md:py-28" data-testid="look-for-section">
        <div className="container-pn">
          <Reveal>
            <SectionHeading eyebrow="Is It A Fit?" title="What We Look For In A Brand Partner" subtitle="We partner with manufacturers seeking to grow. If this sounds like you, let's talk." />
          </Reveal>
          <div className="mt-12 grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3">
            {WHAT_WE_LOOK_FOR.map((w, i) => (
              <Reveal key={w.title} delay={i * 0.04}>
                <div className="flex h-full items-start gap-4 border border-border bg-white p-6">
                  <span className="flex h-9 w-9 shrink-0 items-center justify-center bg-orange/10 text-orange">
                    <Icon name={w.icon} size={18} />
                  </span>
                  <div>
                    <h3 className="font-heading text-base font-semibold text-navy">{w.title}</h3>
                    <p className="mt-1.5 text-sm leading-relaxed text-foreground/70">{w.desc}</p>
                  </div>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* SECTION 6: PARTNERSHIP PROCESS */}
      <section className="bg-white py-20 md:py-28" data-testid="process-section">
        <div className="container-pn">
          <Reveal>
            <SectionHeading eyebrow="How It Works" title="Our Partnership Process" />
          </Reveal>
          <div className="mt-14 grid grid-cols-1 gap-8 lg:grid-cols-5">
            {PROCESS_STEPS.map((s, i) => (
              <Reveal key={s.title} delay={i * 0.08}>
                <div className="relative h-full">
                  <div className="flex h-12 w-12 items-center justify-center rounded-full bg-navy font-heading text-lg font-black text-white">
                    {i + 1}
                  </div>
                  {i < PROCESS_STEPS.length - 1 && (
                    <div className="absolute left-12 top-6 hidden h-px w-[calc(100%-3rem)] bg-border lg:block" />
                  )}
                  <h3 className="mt-5 font-heading text-base font-semibold text-navy">{s.title}</h3>
                  <p className="mt-2 text-sm leading-relaxed text-foreground/70">{s.desc}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* SECTION 7: LET'S GROW TOGETHER */}
      <section className="bg-surface py-20 md:py-28" data-testid="contact-section">
        <div className="container-pn grid grid-cols-1 gap-14 lg:grid-cols-2">
          <Reveal>
            <p className="label-eyebrow text-orange">Let's Grow Together</p>
            <h2 className="mt-3 font-heading text-3xl font-bold tracking-tight text-navy sm:text-4xl">
              Request a partnership review
            </h2>
            <p className="mt-5 max-w-md text-base leading-relaxed text-foreground/70">
              Tell us about your brand and goals. Our partnerships team will review the opportunity and
              show you how Hays Holdings can expand your distribution and scale your sales.
            </p>
            <ul className="mt-8 space-y-3">
              {["Expand distribution nationwide", "Grow Amazon & marketplace revenue", "Protect your brand & pricing", "A long-term strategic partner"].map((t) => (
                <li key={t} className="flex items-center gap-3 text-sm text-foreground/80">
                  <span className="flex h-5 w-5 items-center justify-center bg-orange"><Check size={13} className="text-white" /></span>
                  {t}
                </li>
              ))}
            </ul>
          </Reveal>
          <Reveal delay={0.1}>
            <div className="border border-border bg-white p-8 md:p-10">
              <LeadForm />
            </div>
          </Reveal>
        </div>
      </section>
    </div>
  );
}
